/**
 * React hooks for enhanced officials management with multi-league support
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useLeagueSeasonStore } from '@/state/useLeagueSeasonStore';
import {
  addOfficialToLeague,
  getLeagueOfficials,
  updateLeagueOfficialRelationship,
  bulkUpdateOfficialsLocation,
  requestOfficialModification,
  updateOfficialDirect,
  getPendingModificationRequests,
  reviewModificationRequest,
  checkDuplicateOfficials,
  getUnresolvedDuplicates,
  resolveDuplicate,
  mergeOfficials,
  rescanDuplicateCandidates,
  getOfficialHistory,
  getLeagueActivityHistory,
  getOfficialUpcomingPlanning,
  getOfficialSyntheticActivity,
  getOfficialAssignmentHistory,
  searchOfficialCandidates,
} from '@/services/officialScalingService';
import { getOfficialWriteScope, findOfficialMatches, linkOfficialToLeague } from '@/services/sharedOfficialsService';
import { addOfficialSuccessKey, officialWriteErrorKey, resultErrorMessage, type OfficialIdentifierQuery, type OfficialWriteScope } from '@/lib/officials/sharedOfficials';
import {
  AddOfficialToLeagueRequest,
  OfficialFilters,
  RequestOfficialModificationParams,
  ReviewModificationRequestParams,
  CheckDuplicateOfficialsParams,
  MergeOfficialsParams,
  LeagueOfficial,
  PaginatedOfficialsResponse,
  OfficialPlanningOptions,
  OfficialPlanningSnapshot,
  OfficialActivityEntry,
  OfficialActivityOptions,
  OfficialCandidateQuery,
} from '@/types/officials-scaling.types';
import { useNotificationContext } from '@/contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

// ============= Query Keys =============
const QUERY_KEYS = {
  leagueOfficials: (leagueId: string, filters?: OfficialFilters) =>
    ['league-officials', leagueId, filters],
  modificationRequests: (leagueId?: string) =>
    ['modification-requests', leagueId],
  duplicateCandidates: ['duplicate-candidates'],
  officialHistory: (officialId: string) => ['official-history', officialId],
  officialPlanning: (
    officialId: string,
    options?: OfficialPlanningOptions
  ) => [
      'official-planning',
      officialId,
      options?.leagueId ?? null,
      options?.assignmentsLimit ?? null,
      options?.unavailabilitiesLimit ?? null,
    ],
  officialActivity: (
    officialId: string,
    options?: OfficialActivityOptions
  ) => [
      'official-activity',
      officialId,
      options?.limit ?? null,
      options?.lookbackDays ?? null,
    ],
  officialAssignmentHistory: (
    officialId: string,
    options?: { limit?: number; lookbackDays?: number; leagueId?: string | null }
  ) => [
      'official-assignment-history',
      officialId,
      options?.limit ?? null,
      options?.lookbackDays ?? null,
      options?.leagueId ?? null,
    ],
};

const invalidateLeagueOfficialsQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  leagueIds?: string | string[]
) => {
  const ids = Array.isArray(leagueIds)
    ? leagueIds.filter(Boolean)
    : leagueIds
      ? [leagueIds]
      : [];

  if (ids.length === 0) {
    queryClient.invalidateQueries({ queryKey: ['league-officials'] });
    queryClient.invalidateQueries({ queryKey: ['league-officials-stats'] });
  } else {
    ids.forEach((leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['league-officials', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['league-officials-stats', leagueId] });
    });
  }

  queryClient.invalidateQueries({ queryKey: ['officials'] });
};

// ============= League Officials Hooks =============

/**
 * Hook to get officials for a specific league with pagination support
 */
export function useLeagueOfficials(leagueId: string, filters?: OfficialFilters) {
  return useQuery<PaginatedOfficialsResponse>({
    queryKey: QUERY_KEYS.leagueOfficials(leagueId, filters),
    queryFn: () => getLeagueOfficials(leagueId, filters),
    enabled: !!leagueId,
    placeholderData: keepPreviousData,
    staleTime: 30_000, // 30 seconds - officials don't change frequently
  });
}

/**
 * Hook to add an official to a league
 */
export function useAddOfficialToLeague() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { t } = useTranslation('officials');

  return useMutation({
    mutationFn: (params: AddOfficialToLeagueRequest) =>
      addOfficialToLeague(params),
    onSuccess: (response, variables) => {
      if (response.action === 'needs_confirmation') return;
      if (response.success) {
        // fr + ar, one message per action (created / linked / reactivated / already_member).
        showNotification(t(addOfficialSuccessKey(response.action)), 'success');

        // Invalidate relevant queries
        invalidateLeagueOfficialsQueries(queryClient, variables.leagueId);
      } else {
        // Never a raw RPC code: known ones translated, others generic; RPC text shown as is.
        showNotification(resultErrorMessage(response.error, t, 'add_modal.add_error'), 'error');
      }
    },
    onError: (error: Error) => {
      // Known guard / RPC code translated, anything else the generic message (never English).
      const key = officialWriteErrorKey(error);
      showNotification(t(key ?? 'add_modal.add_error'), 'error');
    },
  });
}

/**
 * Hook to update league-official relationship
 */
export function useUpdateLeagueOfficialRelationship() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();

  return useMutation({
    mutationFn: ({
      leagueId,
      officialId,
      updates,
    }: {
      leagueId: string;
      officialId: string;
      updates: Partial<LeagueOfficial>;
    }) => updateLeagueOfficialRelationship(leagueId, officialId, updates),

    // Optimistic update
    onMutate: async (variables) => {
      const queryKey = ['league-officials', variables.leagueId];

      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<PaginatedOfficialsResponse>(queryKey);

      // Optimistically update the cache
      if (previousData) {
        queryClient.setQueryData<PaginatedOfficialsResponse>(queryKey, (old) => {
          if (!old) return old;

          return {
            ...old,
            data: old.data.map((official) => {
              // Find the league relationship to update
              const leagueIndex = official.leagues.findIndex(
                l => l.leagueId === variables.leagueId
              );

              if (official.id === variables.officialId && leagueIndex !== -1) {
                // Update the specific league relationship
                const updatedLeagues = [...official.leagues];
                updatedLeagues[leagueIndex] = {
                  ...updatedLeagues[leagueIndex],
                  status: variables.updates.status || updatedLeagues[leagueIndex].status,
                  canEdit: variables.updates.canBeEditedByLeague !== undefined
                    ? variables.updates.canBeEditedByLeague
                    : updatedLeagues[leagueIndex].canEdit,
                };

                return {
                  ...official,
                  leagues: updatedLeagues,
                };
              }

              return official;
            }),
          };
        });
      }

      return { previousData };
    },

    onSuccess: (_, variables) => {
      showNotification('Official relationship updated', 'success');
      // Still invalidate to ensure server data is in sync
      invalidateLeagueOfficialsQueries(queryClient, variables.leagueId);
    },

    onError: (error: Error, variables, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(
          ['league-officials', variables.leagueId],
          context.previousData
        );
      }

      showNotification(
        error.message || 'Failed to update relationship',
        'error'
      );
    },
  });
}

export function useBulkUpdateOfficialLocation() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { t } = useTranslation('officials');

  return useMutation({
    mutationFn: ({
      leagueId,
      officialIds,
      locationId,
      address,
    }: {
      leagueId: string;
      officialIds: string[];
      locationId: string;
      address?: string | null;
    }) =>
      bulkUpdateOfficialsLocation(
        officialIds,
        locationId,
        address !== undefined ? { address } : undefined
      ),
    onSuccess: (result, variables) => {
      showNotification(t('scaling.officials_updated', { count: result.updated.length }), "success");
      if (result.skipped.length > 0) {
        showNotification(t('shared.bulk_skipped', { n: result.skipped.length }), "warning");
      }
      invalidateLeagueOfficialsQueries(queryClient, variables.leagueId);
    },
    onError: (error: Error) => {
      const key = officialWriteErrorKey(error);
      showNotification(
        key ? t(key) : (error.message || t('scaling.location_update_failed')),
        "error"
      );
    },
  });
}

// ============= Modification Request Hooks =============

/**
 * Hook to get pending modification requests
 */
export function usePendingModificationRequests(leagueId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: QUERY_KEYS.modificationRequests(leagueId),
    queryFn: () => getPendingModificationRequests(leagueId),
    staleTime: 60_000, // 1 minute - badge counts don't need real-time updates
    enabled,
  });
}

/**
 * Hook to request official modification
 */
export function useRequestOfficialModification() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();

  return useMutation({
    mutationFn: (params: RequestOfficialModificationParams) =>
      requestOfficialModification(params),
    onSuccess: () => {
      showNotification('Modification request submitted', 'success');
      queryClient.invalidateQueries({
        queryKey: ['modification-requests'],
      });
    },
    onError: (error: Error) => {
      showNotification(
        error.message || 'Failed to submit modification request',
        'error'
      );
    },
  });
}

/**
 * Hook for directly updating official data (for creators)
 */
export function useUpdateOfficialDirect() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { t } = useTranslation('officials');

  return useMutation({
    mutationFn: ({
      officialId,
      updates,
    }: {
      officialId: string;
      updates: Record<string, any>;
      leagueIds?: string[];
    }) =>
      updateOfficialDirect(officialId, updates),
    onSuccess: (_, variables) => {
      showNotification(t('scaling.official_updated_success'), 'success');
      invalidateLeagueOfficialsQueries(queryClient, variables.leagueIds);
      queryClient.invalidateQueries({
        queryKey: ['official-history', variables.officialId],
      });
    },
    onError: (error: Error) => {
      const key = officialWriteErrorKey(error);
      showNotification(
        key ? t(key) : (error.message || t('scaling.official_update_failed')),
        'error'
      );
    },
  });
}

/**
 * Hook to review modification requests
 */
export function useReviewModificationRequest() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { t } = useTranslation('officials');

  return useMutation({
    mutationFn: (params: ReviewModificationRequestParams) =>
      reviewModificationRequest(params),
    onSuccess: (_, variables) => {
      const message =
        variables.action === 'approve'
          ? 'Modification request approved'
          : 'Modification request rejected';
      showNotification(message, 'success');

      queryClient.invalidateQueries({
        queryKey: ['modification-requests'],
      });
      invalidateLeagueOfficialsQueries(queryClient);
    },
    onError: (error: Error) => {
      // Approuver applique la modification : les gardes de l'officiel partagé peuvent refuser.
      const key = officialWriteErrorKey(error);
      showNotification(
        key ? t(key) : (error.message || 'Failed to review modification request'),
        'error'
      );
    },
  });
}

// ============= Duplicate Detection Hooks =============

/**
 * Hook to check for duplicate officials
 */
export function useCheckDuplicates() {
  const { showNotification } = useNotificationContext();

  return useMutation({
    mutationFn: (params: CheckDuplicateOfficialsParams) =>
      checkDuplicateOfficials(params),
    onSuccess: (duplicates) => {
      if (duplicates.length > 0) {
        showNotification(
          `Found ${duplicates.length} potential duplicate(s)`,
          'warning'
        );
      }
    },
    onError: (error: Error) => {
      showNotification(
        error.message || 'Failed to check for duplicates',
        'error'
      );
    },
  });
}

/**
 * Hook to get unresolved duplicate candidates
 */
export function useUnresolvedDuplicates() {
  // Paires de la ligue sélectionnée : la liste et la pastille lisent ce même hook.
  const currentLeagueId = useLeagueSeasonStore((state) => state.currentLeagueId) || undefined;
  return useQuery({
    queryKey: [...QUERY_KEYS.duplicateCandidates, currentLeagueId ?? 'all'],
    queryFn: () => getUnresolvedDuplicates(currentLeagueId),
    staleTime: 60_000, // 1 minute - badge counts don't need real-time updates
  });
}

/**
 * Hook to resolve duplicate candidates
 */
export function useResolveDuplicate() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();

  return useMutation({
    mutationFn: ({
      duplicateId,
      resolution,
      mergeIntoId,
      notes,
    }: {
      duplicateId: string;
      resolution: 'confirmed_duplicate' | 'not_duplicate' | 'merge';
      mergeIntoId?: string;
      notes?: string;
    }) => resolveDuplicate(duplicateId, resolution, mergeIntoId, notes),
    onSuccess: () => {
      showNotification('Duplicate resolved', 'success');
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.duplicateCandidates,
      });
    },
    onError: (error: Error) => {
      showNotification(
        error.message || 'Failed to resolve duplicate',
        'error'
      );
    },
  });
}

/**
 * Hook to merge officials
 */
export function useMergeOfficials() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { t } = useTranslation('officials');

  return useMutation({
    mutationFn: (params: MergeOfficialsParams) => mergeOfficials(params),
    onSuccess: (_, variables) => {
      showNotification('Officials merged successfully', 'success');
      invalidateLeagueOfficialsQueries(queryClient, variables?.relatedLeagueIds);
      queryClient.invalidateQueries({
        queryKey: ['officials'],
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.duplicateCandidates,
      });
    },
    onError: (error: Error) => {
      const key = officialWriteErrorKey(error);
      showNotification(
        key ? t(key) : error.message || 'Failed to merge officials',
        'error'
      );
    },
  });
}

export function useRescanDuplicateCandidates() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { t } = useTranslation('officials');

  return useMutation<{ inserted: number }, Error, number | undefined>({
    mutationFn: (limit?: number) => rescanDuplicateCandidates(limit),
    onSuccess: (result) => {
      if (result.inserted > 0) {
        showNotification(`${result.inserted} new duplicate(s) detected`, 'success');
      } else {
        showNotification('Duplicate scan completed — no new matches', 'success');
      }

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.duplicateCandidates,
      });
    },
    onError: (error: Error & { code?: string; details?: string }) => {
      if (error?.code === 'PGRST202') {
        showNotification(
          t('scaling.rescan_not_configured'),
          'warning'
        );
        return;
      }

      showNotification(
        error.message || 'Failed to rescan duplicates',
        'error'
      );
    },
  });
}

// ============= History Hooks =============

/**
 * Hook to get official history
 */
export function useOfficialHistory(officialId: string, limit: number = 50) {
  return useQuery({
    queryKey: QUERY_KEYS.officialHistory(officialId),
    queryFn: () => getOfficialHistory(officialId, limit),
    enabled: !!officialId,
  });
}

/**
 * Hook to get league-wide activity history
 */
export function useLeagueActivityHistory(leagueId: string, limit: number = 100) {
  return useQuery({
    queryKey: ['league-activity-history', leagueId, limit],
    queryFn: () => getLeagueActivityHistory(leagueId, limit),
    enabled: !!leagueId,
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useOfficialPlanning(
  officialId: string,
  options?: OfficialPlanningOptions
) {
  return useQuery<OfficialPlanningSnapshot>({
    queryKey: QUERY_KEYS.officialPlanning(officialId, options),
    queryFn: () => getOfficialUpcomingPlanning(officialId, options),
    enabled: !!officialId,
  });
}

export function useOfficialActivity(
  officialId: string,
  options?: OfficialActivityOptions
) {
  return useQuery<OfficialActivityEntry[]>({
    queryKey: QUERY_KEYS.officialActivity(officialId, options),
    queryFn: () => getOfficialSyntheticActivity(officialId, options),
    enabled: !!officialId,
  });
}

export function useOfficialAssignmentHistory(
  officialId: string,
  options?: { limit?: number; lookbackDays?: number; leagueId?: string | null }
) {
  return useQuery({
    queryKey: QUERY_KEYS.officialAssignmentHistory(officialId, options),
    queryFn: () => getOfficialAssignmentHistory(officialId, options),
    enabled: !!officialId,
  });
}

/**
 * Existing people resembling the name typed in the add form.
 */
export function useOfficialCandidates(query: OfficialCandidateQuery) {
  return useQuery({
    queryKey: ['official-candidates', query],
    queryFn: () => searchOfficialCandidates(query),
    enabled: (query.lastName ?? '').trim().length >= 2 || (query.lastNameAr ?? '').trim().length >= 2,
    staleTime: 1000 * 60,
  });
}

// ============= Shared officials (several leagues) =============

const EMPTY_WRITE_SCOPE = new Map<string, OfficialWriteScope>();

/** Shared / writable flags of the listed officials (only those of the caller's leagues). */
export function useOfficialWriteScope(officialIds: string[]): Map<string, OfficialWriteScope> {
  const ids = useMemo(() => Array.from(new Set(officialIds.filter(Boolean))).sort(), [officialIds]);
  // null (RPC not deployed yet) → empty map: no badge, today's edit routing, no retry.
  const { data } = useQuery({
    queryKey: ['official-write-scope', ids],
    queryFn: () => getOfficialWriteScope(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
  return data ?? EMPTY_WRITE_SCOPE;
}

export function useFindOfficialMatches() {
  return useMutation({
    mutationFn: ({ leagueId, query }: { leagueId: string; query: OfficialIdentifierQuery }) =>
      findOfficialMatches(leagueId, query),
  });
}

export function useLinkOfficialToLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leagueId, officialId }: { leagueId: string; officialId: string }) =>
      linkOfficialToLeague(leagueId, officialId),
    onSuccess: (_result, variables) => {
      invalidateLeagueOfficialsQueries(queryClient, variables.leagueId);
      queryClient.invalidateQueries({ queryKey: ['officials'] });
      queryClient.invalidateQueries({ queryKey: ['official-write-scope'] });
    },
  });
}
